// src/components/RecipeOrderPlanner.js

import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

export default function RecipeOrderPlanner() {
  const [recipes, setRecipes] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [pansByRecipe, setPansByRecipe] = useState({});
  const [rows, setRows] = useState([]);

  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  useEffect(() => {
    const fetchData = async () => {
      const recipesSnap = await getDocs(collection(db, 'recipes'));
      const stockSnap = await getDocs(collection(db, 'stock_store'));

      const recipeList = recipesSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const stock = {};
      stockSnap.forEach((docSnap) => {
        stock[docSnap.id] = docSnap.data();
      });

      setRecipes(recipeList);
      setStockMap(stock);
    };

    fetchData();
  }, []);

  const selectedRecipes = useMemo(() => {
    return recipes
      .map((recipe) => ({
        ...recipe,
        plannedPans: Number(pansByRecipe[recipe.id] || 0),
      }))
      .filter((recipe) => recipe.plannedPans > 0);
  }, [recipes, pansByRecipe]);

  const shouldIgnoreIngredient = (ingredient) => {
    const product = String(ingredient?.product || '').trim().toLowerCase();

    return (
      !ingredient?.code ||
      product.includes('pulp') ||
      product.startsWith('water')
    );
  };

  const generatePlan = () => {
    const ingredientMap = {};

    selectedRecipes.forEach((recipe) => {
      const pans = Number(recipe.plannedPans || 0);

      (recipe.ingredients || [])
        .filter((ingredient) => !shouldIgnoreIngredient(ingredient))
        .forEach((ingredient) => {
          const code = String(ingredient.code || '').trim();
          const product = ingredient.product || code;
          const requiredKg =
            (Number(ingredient.amount_g || 0) * pans) / 1000;

          if (!ingredientMap[code]) {
            ingredientMap[code] = {
              code,
              ingredient: product,
              requiredKg: 0,
              usedIn: [],
            };
          }

          ingredientMap[code].requiredKg += requiredKg;
          ingredientMap[code].usedIn.push(
            `${recipe.name}: ${pans} pan${pans !== 1 ? 's' : ''}`
          );
        });
    });

    const result = Object.values(ingredientMap)
      .map((item) => {
        const stock = stockMap[item.code] || {};
        const boxWeight = Number(stock.boxWeight || 0);

        const boxesToBuy =
          boxWeight > 0 ? Math.ceil(item.requiredKg / boxWeight) : 0;

        const boughtKg = boxesToBuy * boxWeight;
        const extraKg = boughtKg - item.requiredKg;

        return {
          ...item,
          requiredKg: round2(item.requiredKg),
          boxWeight: round2(boxWeight),
          boxesToBuy,
          boughtKg: round2(boughtKg),
          extraKg: round2(extraKg),
        };
      })
      .sort((a, b) => b.requiredKg - a.requiredKg);

    setRows(result);
  };

  const clearPlan = () => {
    setPansByRecipe({});
    setRows([]);
  };

  const exportCSV = () => {
    if (!rows.length) return;

    const headers = [
      'Code',
      'Ingredient',
      'Required Kg',
      'Box Weight Kg',
      'Boxes To Buy',
      'Bought Kg',
      'Extra Kg',
      'Used In',
    ];

    const csvRows = rows.map((r) => [
      r.code,
      r.ingredient,
      r.requiredKg.toFixed(2),
      r.boxWeight.toFixed(2),
      r.boxesToBuy,
      r.boughtKg.toFixed(2),
      r.extraKg.toFixed(2),
      `"${r.usedIn.join(' | ')}"`,
    ]);

    const csv = [
      headers.join(','),
      ...csvRows.map((row) => row.join(',')),
    ].join('\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'recipe_order_planner.csv';
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="recipe-order-planner">
      <h2>🛒 Recipe Order Planner</h2>

      <p style={{ marginBottom: 20 }}>
        Enter how many pans you want to make. This calculates ingredients from scratch and ignores current stock.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}
      >
        {recipes.map((recipe) => (
          <div
            key={recipe.id}
            style={{
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: 12,
              background: '#fff',
            }}
          >
            <label>
              <strong>{recipe.name}</strong>
            </label>

            <input
              type="number"
              min="0"
              step="1"
              placeholder="Pans"
              value={pansByRecipe[recipe.id] || ''}
              onChange={(e) =>
                setPansByRecipe((prev) => ({
                  ...prev,
                  [recipe.id]: e.target.value,
                }))
              }
              style={{
                width: '100%',
                marginTop: 8,
                padding: 8,
              }}
            />
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        <button
          className="btn btn-primary"
          onClick={generatePlan}
          disabled={!selectedRecipes.length}
        >
          Generate Order Plan
        </button>

        <button className="btn" onClick={clearPlan}>
          Clear
        </button>

        {rows.length > 0 && (
          <button className="btn" onClick={exportCSV}>
            📥 Export CSV
          </button>
        )}
      </div>

      {selectedRecipes.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <strong>Selected:</strong>{' '}
          {selectedRecipes
            .map((r) => `${r.name} (${r.plannedPans} pan${r.plannedPans !== 1 ? 's' : ''})`)
            .join(', ')}
        </div>
      )}

      {rows.length > 0 && (
        <table
          border="1"
          cellPadding="8"
          style={{
            width: '100%',
            textAlign: 'center',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              <th>Code</th>
              <th>Ingredient</th>
              <th>Required kg</th>
              <th>Box kg</th>
              <th>Boxes To Buy</th>
              <th>Bought kg</th>
              <th>Extra kg</th>
              <th>Used In</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr key={r.code}>
                <td>{r.code}</td>
                <td>{r.ingredient}</td>
                <td>{r.requiredKg.toFixed(2)}</td>
                <td>{r.boxWeight.toFixed(2)}</td>
                <td>
                  <strong>{r.boxesToBuy}</strong>
                </td>
                <td>{r.boughtKg.toFixed(2)}</td>
                <td>{r.extraKg.toFixed(2)}</td>
                <td style={{ textAlign: 'left' }}>
                  {r.usedIn.join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}